import React, {
  ComponentProps,
  HTMLAttributes,
  RefObject,
  RefCallback,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';

import {
  useVirtualizer,
  ScrollToOptions,
  defaultRangeExtractor,
  Range,
} from '@tanstack/react-virtual';

import { AlloySuspense } from '@adsk/alloy-react-suspense';
import theme from '@adsk/alloy-react-theme';

import DropdownMenu from './DropdownMenu';
import VirtualizedMenuItem from './VirtualizedMenuItem';
import { isGroupHeader as defaultIsGroupHeader } from '../../helpers';
import {
  defaultRenderFooter,
  defaultRenderNoAutocompleteResults,
  defaultRenderVirtualGroupHeader,
} from '../../helpers/defaultRenderers';
import { SuspenseFallback } from '../../layout';
import { Menu, GroupFilter } from '../../layout/Menu';
import type {
  GenericItem,
  GetItemProps,
  GroupHeader,
  RenderGroupHeaderProps,
  RenderMenuItem,
  VirtualProps,
} from '../../types';

type VirtualItem = {
  index: number;
  size: number;
  start: number;
};

type WithRefObject = {
  /**
   * @deprecated use `setScrollElement` instead.
   * A ref to the parent element of the virtualized menu.
   * This is returned in `virtualProps` from dropdown hooks
   */
  parentRef: React.RefObject<HTMLDivElement>;
  /**
   * A callback ref that sets the scroll element of the virtualized menu.
   */
  setScrollElement?: never;
};

type WithRefCallback = {
  /**
   * @deprecated use `setScrollElement` instead.
   * A ref to the parent element of the virtualized menu.
   * This is returned in `virtualProps` from dropdown hooks
   */
  parentRef?: never;
  /**
   * A callback ref that sets the scroll element of the virtualized menu.
   */
  setScrollElement: RefCallback<HTMLElement>;
};

type VirtualizedMenuProps<TItem extends GenericItem> = ComponentProps<
  typeof Menu
> &
  (WithRefObject | WithRefCallback) & {
    /**
     * The total number of items in the dropdown.
     * This is returned from `@tanstack/react-virtual`'s `useVirtual` hook.
     */
    totalSize: number;
    /**
     * The items to be rendered in the virtualized menu.
     * This is returned from `@tanstack/react-virtual`'s `useVirtual` hook.
     */
    virtualItems: VirtualItem[];
    getItemProps: GetItemProps<TItem>;
    isGroupHeader?: (item: unknown) => item is GroupHeader;
    menuItems: TItem[];
    renderMenuItem?: RenderMenuItem<TItem>;
    removeSelectedItem?: (item: TItem) => void;
    /**
     * @deprecated This is now returned from getItemProps
     */
    highlightedIndex?: number;
    allowCollapsibleGroups?: boolean;
    allowGroupFiltering?: boolean;
    showGroupItemCount?: boolean;
    setExpandedGroupKey?: (key: string) => void;
    expandedGroupKey?: string;
    scrollToIndex?: (index: number, options?: ScrollToOptions) => void;
    visibleVirtualRange?: Range | null;
    renderGroupHeader?: (props: RenderGroupHeaderProps) => React.ReactNode;
    setSelectedGroupKey?: (key: string) => void;
    selectedGroupKey?: string;
    showGroupHeaders?: boolean;
    setHighlightedIndex?: (index: number) => void;
    refToFocusOnClick?: RefObject<HTMLElement>;
    showEmptyGroups?: boolean;
    dropdownId?: string;
  };

const defaultRenderMenuItem = <TItem extends GenericItem>({
  ...itemProps
}: Parameters<RenderMenuItem<TItem>>[0]) => (
  <VirtualizedMenuItem {...itemProps} />
);

const noResultsHeight = 32;
const loadingSpinnerHeight = 24 + theme.spacing.M * 2;

const VirtualizedMenu = <TItem extends GenericItem>({
  parentRef,
  setScrollElement,
  totalSize,
  virtualItems,
  getItemProps,
  isGroupHeader = defaultIsGroupHeader,
  menuItems,
  renderHeader,
  renderMenuItem = defaultRenderMenuItem,
  renderFooter = defaultRenderFooter,
  allowCollapsibleGroups,
  allowGroupFiltering,
  setExpandedGroupKey,
  expandedGroupKey,
  scrollToIndex,
  visibleVirtualRange,
  showGroupItemCount,
  renderGroupHeader = defaultRenderVirtualGroupHeader,
  selectedGroupKey,
  setSelectedGroupKey,
  showGroupHeaders = true,
  setHighlightedIndex,
  refToFocusOnClick,
  showEmptyGroups = false,
  dropdownId,
  ...props
}: VirtualizedMenuProps<TItem>) => {
  const renderWrapper = useCallback(
    ({ style, ...wrapperProps }: HTMLAttributes<HTMLDivElement>) => (
      <div
        ref={setScrollElement || parentRef}
        style={{
          height:
            // if there are no items found with autocomplete and a custom render
            // is provided, set height to undefined so that the menu will take
            // on the height of whatever custom render is provided
            props.isAutocomplete &&
            props.renderNoAutocompleteResults !==
              defaultRenderNoAutocompleteResults &&
            totalSize <= 0
              ? undefined
              : totalSize,
          ...(props.isAutocomplete &&
            props.isOpen && { minHeight: noResultsHeight }),
          ...(props.isLoading &&
            props.isOpen && { minHeight: loadingSpinnerHeight }),
          ...style,
          overflowX: 'hidden',
        }}
        {...wrapperProps}
      />
    ),
    [
      parentRef,
      props.isAutocomplete,
      props.isLoading,
      props.isOpen,
      setScrollElement,
      totalSize,
      props.renderNoAutocompleteResults,
    ],
  );
  const { isAutocomplete } = props;

  const virtualizedMenuItems = useMemo(
    () =>
      virtualItems.map(({ index, size, start }) => {
        const item = menuItems[index];
        if (isGroupHeader?.(item)) {
          if (
            !showGroupHeaders ||
            (!showEmptyGroups && !item.filteredItemCount)
          ) {
            return null;
          }
          return renderGroupHeader?.({
            item,
            index,
            expandedGroupKey,
            allowCollapsibleGroups,
            isAutocomplete,
            setExpandedGroupKey,
            showGroupItemCount,
            start,
            height: size,
            scrollToViewVirtual: () => {
              scrollToIndex?.(index, {
                align: 'center',
                behavior: 'smooth',
              });
            },
            isVisibleVirtual: () => {
              if (visibleVirtualRange) {
                return (
                  index >= visibleVirtualRange.startIndex &&
                  index <= visibleVirtualRange.endIndex
                );
              }

              return false;
            },
          });
        }

        return renderMenuItem?.({
          key: index,
          ...getItemProps({ item }),
          item,
          start,
          height: size,
        });
      }),
    [
      allowCollapsibleGroups,
      expandedGroupKey,
      getItemProps,
      isGroupHeader,
      menuItems,
      renderMenuItem,
      scrollToIndex,
      setExpandedGroupKey,
      virtualItems,
      visibleVirtualRange,
      showGroupItemCount,
      isAutocomplete,
      renderGroupHeader,
      showGroupHeaders,
      showEmptyGroups,
    ],
  );

  return (
    <Menu
      {...props}
      renderWrapper={renderWrapper}
      renderFooter={renderFooter}
      renderHeader={
        allowGroupFiltering
          ? () => (
              <AlloySuspense fallback={<SuspenseFallback />}>
                <GroupFilter
                  menuItems={menuItems}
                  selectedGroupKey={selectedGroupKey}
                  setSelectedGroupKey={setSelectedGroupKey}
                  showGroupItemCount={showGroupItemCount}
                  isAutocomplete={isAutocomplete}
                  setHighlightedIndex={setHighlightedIndex}
                  refToFocusOnClick={refToFocusOnClick}
                  dropdownId={dropdownId}
                />
              </AlloySuspense>
            )
          : renderHeader
      }
    >
      {virtualizedMenuItems}
    </Menu>
  );
};

type MenuProps<TItem extends GenericItem> = ComponentProps<
  typeof DropdownMenu<TItem>
>;

function VirtualizedMenuRender<TItem extends GenericItem>(
  menuProps: MenuProps<TItem>,
  virtualProps: VirtualProps,
) {
  // this doesn't seem to work unless this is a ref, it could be a quirk of react-virtual
  // as the example they show is this as well: https://github.com/TanStack/virtual/discussions/220
  const visibleRange = useRef<Range | null>(null);

  const { getTotalSize, getVirtualItems, scrollToIndex, measure } =
    useVirtualizer({
      rangeExtractor: useCallback((range: Range) => {
        if (range) {
          // keep track of range for internal use so that
          // we can determine the true visibible index range
          // of items
          visibleRange.current = range;
        }

        return defaultRangeExtractor(range);
      }, []),
      ...virtualProps,
    });

  // force measuring the virtual items if estimate size changes
  useLayoutEffect(() => {
    measure?.();
  }, [measure, virtualProps.estimateSize]);

  return (
    <VirtualizedMenu
      {...menuProps}
      totalSize={getTotalSize()}
      virtualItems={getVirtualItems()}
      setScrollElement={virtualProps.setScrollElement}
      scrollToIndex={scrollToIndex}
      visibleVirtualRange={visibleRange.current}
    />
  );
}

VirtualizedMenu.render = VirtualizedMenuRender;

export default VirtualizedMenu;
